import {useActiveVkuiLocation, useBlocker, useFirstPageCheck, useRouteNavigator} from "@vkontakte/vk-mini-apps-router";
import {Button, PanelHeader, PanelHeaderBack, PopoutWrapper, ScreenSpinner, Separator, SplitCol, SplitLayout} from "@vkontakte/vkui";
import {observer} from "mobx-react-lite";
import React, {useCallback, useEffect, useRef, useState} from "react";
import Confetti from "react-confetti";
import useWindowSize from "react-use/lib/useWindowSize";
import {GAME_PANEL, GAME_RESULTS_MODAL} from "../../routing/routing";
import {
    buyHint,
    createGame,
    FINISH_REASON_TIMEOUT,
    finishGame,
    getActiveGame,
    submitWord,
    useHint,
    WORD_SEARCH_MODE,
} from "../../services/game";
import {add3DailyAttempts, getDailyAttemptsPrice, getPlayerInfo} from "../../services/player";
import GameStore from "../../store/game";
import {GameBoard} from "./GameBoard";
import {StatusStrip} from "./StatusStrip";
import {HintPanel} from "./HintPanel";
import WordTargetsPanel from "./WordTargetsPanel";
import {useToast} from "../../context/ToastContext";
import BonusStore from "../../store/bonus";
import {Add3DailyAttemptsBonusPrice} from "../../model/prices";
import {numberWordSuffix} from "../../utils/word";
import AttemptsPanel from "./AttemptsPanel";
import {
    MODAL_CARD_CLASS,
    MODAL_HEADER_CLASS,
    MODAL_PRIMARY_BUTTON_CLASS,
    MODAL_SECONDARY_BUTTON_CLASS,
    MODAL_SUBHEADER_CLASS,
} from "../../modals/modalStyles";
import {getBackendErrorMessage} from "../../utils/backendErrorMessages";

const errUnknownLoadingGame = "ERR_INTERNAL_LOADING_GAME_ERROR";
const errPlayerDailyAttemptsAreEmpty = "ERR_PLAYER_DAILY_ATTEMPTS_ARE_EMPTY";
const DEFAULT_GAME_TIME_SECONDS = 60;
const COMBO_WINDOW_MS = 10000;
const WORD_CONTOUR_HINT_TYPE = "word_contour";
const WORD_CONTOUR_REVEAL_MS = 3000;

const BUTTON_CLASSES = "app-btn-primary cursor-pointer";
const isDevEnv = process.env.REACT_APP_ENV === "dev" || process.env.NODE_ENV === "development";

// Приводит поле к виду 7x7, поддерживая строковый и матричный формат от сервера.
const normalizeGridPayload = (rawGrid) => {
    if (!Array.isArray(rawGrid)) {
        return [];
    }

    return rawGrid
        .map((row) => {
            if (typeof row === "string") {
                return row.split("");
            }

            if (Array.isArray(row)) {
                return row;
            }

            return [];
        })
        .map((row) => row.map((cell) => String(cell ?? "").slice(0, 1).toLowerCase()));
};

// Поддерживает как обёрнутый, так и плоский ответ API активной игры.
const extractGamePayload = (responseData) => {
    const payload = responseData?.response ?? responseData;
    if (!payload) {
        return {isActive: false, game: null};
    }

    if (payload.game) {
        return {
            isActive: payload.isActive ?? payload.is_active ?? payload.game?.isActive ?? payload.game?.is_active ?? false,
            game: payload.game,
        };
    }

    return {
        isActive: payload.isActive ?? payload.is_active ?? false,
        game: payload,
    };
};

export const Game = observer(() => {
    const routeNavigator = useRouteNavigator();
    const isFirstPage = useFirstPageCheck();
    const {panel: activePanel} = useActiveVkuiLocation();

    const [popout, setPopout] = useState(<ScreenSpinner size="large"/>);
    const [isFinishingGame, setIsFinishingGame] = useState(false);
    const [isGameSessionLoaded, setIsGameSessionLoaded] = useState(false);
    const [hasShownResultsModal, setHasShownResultsModal] = useState(false);
    const {showErrorToast, showSuccessToast} = useToast();
    const [loadingGameErrorCode, setLoadingGameErrorCode] = useState("");
    const [dailyAttemptPrice, setDailyAttemptPrice] = useState(Add3DailyAttemptsBonusPrice);
    const [isStartingNewGame, setIsStartingNewGame] = useState(false);
    const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
    const [isConfettiVisible, setIsConfettiVisible] = useState(false);
    const [confettiKey, setConfettiKey] = useState(0);
    const [isExitAlertOpen, setIsExitAlertOpen] = useState(false);
    const [isSubmittingWord, setIsSubmittingWord] = useState(false);
    const [hintRevealPath, setHintRevealPath] = useState([]);

    const allowExitOnceRef = useRef(false);
    const confettiTimeoutRef = useRef(null);
    const hintRevealTimeoutRef = useRef(null);
    const availableAttempts = Number(GameStore.dailyAttempts) || 0;
    const {width, height} = useWindowSize();

    const triggerConfetti = () => {
        setConfettiKey((prevKey) => prevKey + 1);
        setIsConfettiVisible(true);
        if (confettiTimeoutRef.current) {
            clearTimeout(confettiTimeoutRef.current);
        }
        confettiTimeoutRef.current = setTimeout(() => {
            setIsConfettiVisible(false);
        }, 7000);
    };

    const stopConfetti = () => {
        if (confettiTimeoutRef.current) {
            clearTimeout(confettiTimeoutRef.current);
            confettiTimeoutRef.current = null;
        }
        setIsConfettiVisible(false);
    };

    // Сбрасывает временную подсветку контура слова от подсказки.
    const clearHintReveal = useCallback(() => {
        if (hintRevealTimeoutRef.current) {
            clearTimeout(hintRevealTimeoutRef.current);
            hintRevealTimeoutRef.current = null;
        }
        setHintRevealPath([]);
    }, []);

    const refreshPlayerInfo = () => {
        return getPlayerInfo().then((resp) => {
            const player = resp.data.response;
            const totalDailyAttempts = player.available_daily_attempts ?? player.daily_attempts ?? 0;
            GameStore.setDailyAttempts(totalDailyAttempts);
            if (player.best_score !== undefined || player.bestScore !== undefined) {
                GameStore.setBestScore(player.best_score ?? player.bestScore);
            }
            if (player.hintBalances || player.hint_balances) {
                GameStore.setPlayerHintBalances(player.hintBalances ?? player.hint_balances);
            }
            if (player.coins !== undefined) {
                BonusStore.setCoins(player.coins);
            }
        }).catch(() => {
            showErrorToast("Не удалось обновить данные игрока");
        });
    };

    const fetchDailyAttemptPrice = () => {
        getDailyAttemptsPrice().then((resp) => {
            const price = resp?.data?.response?.price;
            if (Number.isFinite(price)) {
                setDailyAttemptPrice(price);
            }
        }).catch(() => {
            setDailyAttemptPrice(Add3DailyAttemptsBonusPrice);
        });
    };

    const normalizeHintState = (rawWallet, rawLimits) => {
        const normalizedLimits = {};
        const normalizedWallet = {};

        if (rawLimits && typeof rawLimits === "object") {
            Object.entries(rawLimits).forEach(([hint, value]) => {
                const max = Number(value?.max ?? value?.total ?? value?.limit ?? 0) || 0;
                let used = Number(value?.used ?? value?.spent ?? 0);
                if (!Number.isFinite(used) || used < 0) {
                    used = 0;
                }
                normalizedLimits[hint] = {max, used};
            });
        }

        if (rawWallet && typeof rawWallet === "object") {
            Object.entries(rawWallet).forEach(([hint, available]) => {
                const remaining = Number(available) || 0;
                normalizedWallet[hint] = remaining < 0 ? 0 : remaining;
            });
        }

        Object.entries(normalizedLimits).forEach(([hint, limit]) => {
            if (normalizedWallet[hint] === undefined) {
                const remaining = Math.max(limit.max - limit.used, 0);
                normalizedWallet[hint] = remaining;
            }
        });

        if (Object.keys(normalizedLimits).length === 0 && Object.keys(normalizedWallet).length > 0) {
            Object.entries(normalizedWallet).forEach(([hint, remaining]) => {
                normalizedLimits[hint] = {max: remaining, used: 0};
            });
        }

        return {wallet: normalizedWallet, limits: normalizedLimits};
    };

    // Заполняет стор состоянием игры из backend payload.
    const hydrateGameState = (game) => {
        if (!game) {
            return;
        }

        const gameId = game.gameId ?? game.id ?? game.GameID ?? game.gameID;
        const parsedId = Number(gameId);
        if (Number.isFinite(parsedId) && parsedId > 0) {
            GameStore.setGameID(parsedId);
        }

        GameStore.setIsActive(game.isActive ?? game.is_active ?? true);

        if (game.score !== undefined) {
            GameStore.setScore(game.score);
        }

        const bestScore = game.bestScore ?? game.best_score;
        if (bestScore !== undefined) {
            GameStore.setBestScore(bestScore);
        }

        const combo = game.comboCount ?? game.currentComboCount;
        if (combo !== undefined) {
            GameStore.setComboCount(combo);
        }

        const lastWordFoundAt = game.lastWordFoundAt ?? game.last_word_found_at;
        if (combo > 0 && lastWordFoundAt) {
            const parsed = Date.parse(lastWordFoundAt);
            if (Number.isFinite(parsed)) {
                if (Date.now() - parsed <= COMBO_WINDOW_MS) {
                    GameStore.setLastComboTimestamp(parsed);
                } else {
                    GameStore.resetCombo();
                }
            }
        }

        if (Array.isArray(game.foundWords)) {
            GameStore.setFoundWords(game.foundWords);
        }

        const targetWordLengths = game.targetWordLengths ?? game.target_word_lengths;
        if (Array.isArray(targetWordLengths)) {
            GameStore.setTargetWordLengths(targetWordLengths);
        } else if (Array.isArray(game.words)) {
            GameStore.setTargetWordLengths(game.words.map((item) => String(item).length));
        }

        const foundWordPaths = game.foundWordPaths ?? game.found_word_paths;
        if (Array.isArray(foundWordPaths)) {
            GameStore.setFoundWordPaths(foundWordPaths);
        }
        const targetWordPaths = game.targetWordPaths ?? game.target_word_paths;
        if (Array.isArray(targetWordPaths)) {
            GameStore.setTargetWordPaths(targetWordPaths);
        }

        const hintWallet = game.hintWallet ?? game.hint_wallet;
        const hintLimits = game.hintLimits ?? game.hint_limits;
        const {wallet, limits} = normalizeHintState(hintWallet, hintLimits);
        GameStore.setHintWallet(wallet);
        GameStore.setHintLimits(limits);

        const baseTimeLimit = game.timeLimitSeconds ?? game.time_limit_seconds;
        const extraTime = game.extraTimeSeconds ?? game.extra_time_seconds ?? 0;
        if (baseTimeLimit !== undefined) {
            GameStore.setTimeLimitSeconds(baseTimeLimit + extraTime);
        }

        const serverLeft = game.serverTimeLeft ?? game.timeLeft ?? game.time_left;
        if (serverLeft !== undefined) {
            GameStore.setTimeLeft(serverLeft);
        } else if (baseTimeLimit !== undefined) {
            GameStore.setTimeLeft(baseTimeLimit + extraTime);
        }

        const gridPayload = game.grid ?? game.grid_letters ?? game.gridLetters;
        if (gridPayload !== undefined) {
            const normalizedGrid = normalizeGridPayload(gridPayload);
            GameStore.setGrid(normalizedGrid);
        }

        const gridSize = game.gridSize ?? game.grid_size;
        if (gridSize !== undefined) {
            GameStore.setGridSize(gridSize);
        }
    };

    const startGameSession = (options = {}) => {
        const showSpinner = options.showSpinner !== false;
        setLoadingGameErrorCode("");
        stopConfetti();
        setIsGameSessionLoaded(false);
        if (showSpinner) {
            setPopout(<ScreenSpinner size="large"/>);
        }
        clearHintReveal();
        GameStore.flush();

        return createGame({mode: WORD_SEARCH_MODE}).then((resp) => {
            const game = resp?.data?.response ?? resp?.data;
            hydrateGameState(game);
            GameStore.clearSelection();
            setIsGameSessionLoaded(true);
        }).catch((err) => {
            if (err.response !== undefined) {
                const errCode = err.response.data.response.code;
                const errMsg = getBackendErrorMessage(err, "Не удалось загрузить игру");
                if (errCode === errPlayerDailyAttemptsAreEmpty) {
                    GameStore.setDailyAttempts(0);
                }
                setLoadingGameErrorCode(errCode);
                showErrorToast(errMsg);
                return;
            }

            setLoadingGameErrorCode(errUnknownLoadingGame);
        }).finally(() => {
            setIsGameSessionLoaded(true);
            if (showSpinner) {
                setPopout(null);
            }
        });
    };

    const loadGameSession = () => {
        setLoadingGameErrorCode("");
        setPopout(<ScreenSpinner size="large"/>);
        setIsGameSessionLoaded(false);
        clearHintReveal();

        return getActiveGame().then((resp) => {
            const {isActive, game} = extractGamePayload(resp?.data);

            if (isActive && game) {
                GameStore.flush();
                hydrateGameState(game);
                setShouldShowOnboarding(false);
                setIsGameSessionLoaded(true);
                return;
            }

            GameStore.flush();
            GameStore.setIsActive(false);
            setShouldShowOnboarding(true);
            setIsGameSessionLoaded(true);
        }).catch((err) => {
            const errMsg = getBackendErrorMessage(err, "Не удалось загрузить игру");
            showErrorToast(errMsg);
        }).finally(() => {
            setIsGameSessionLoaded(true);
            setPopout(null);
        });
    };

    useEffect(() => {
        const timer = setInterval(() => {
            if (!GameStore.isActive) return;
            const current = GameStore.timeLeft;
            if (current > 0) {
                GameStore.setTimeLeft(current - 1);
            }

            const lastComboTimestamp = GameStore.lastComboTimestamp;
            if (lastComboTimestamp && GameStore.comboCount > 0) {
                if (Date.now() - lastComboTimestamp >= COMBO_WINDOW_MS) {
                    GameStore.resetCombo();
                }
            }
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        return () => {
            if (confettiTimeoutRef.current) {
                clearTimeout(confettiTimeoutRef.current);
            }
            if (hintRevealTimeoutRef.current) {
                clearTimeout(hintRevealTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (GameStore.isActive) {
            setHasShownResultsModal(false);
        }
    }, [GameStore.isActive]);

    useEffect(() => {
        if (!shouldShowOnboarding || GameStore.isActive) {
            return;
        }

        const balances = GameStore.playerHintBalances || {};
        if (Object.keys(balances).length === 0) {
            GameStore.setHintWallet({});
            GameStore.setHintLimits({});
        } else {
            GameStore.setHintWallet(balances);
            GameStore.setHintLimits({});
        }

        const baseTime = GameStore.timeLimitSeconds > 0 ? GameStore.timeLimitSeconds : DEFAULT_GAME_TIME_SECONDS;
        if (GameStore.timeLimitSeconds !== baseTime) {
            GameStore.setTimeLimitSeconds(baseTime);
        }
        if (GameStore.timeLeft <= 0) {
            GameStore.setTimeLeft(baseTime);
        }
    }, [shouldShowOnboarding, GameStore.playerHintBalances, GameStore.isActive]);

    useEffect(() => {
        const timeLeft = Number(GameStore.timeLeft) || 0;
        const hasValidGameID = GameStore.gameID && Number.isFinite(Number(GameStore.gameID));

        if (!isGameSessionLoaded || !hasValidGameID || timeLeft > 0 || !GameStore.isActive || isFinishingGame) {
            return;
        }

        setIsFinishingGame(true);
        GameStore.setIsActive(false);
        GameStore.setTimeLeft(0);
        GameStore.clearSelection();

        finishGame(GameStore.gameID, FINISH_REASON_TIMEOUT).then((resp) => {
            const data = resp?.data?.response ?? resp?.data;
            if (!data) {
                showErrorToast("Не удалось завершить игру");
                return;
            }

            if (data.score !== undefined) {
                GameStore.setScore(data.score);
            }

            if (data.coinsReward !== undefined) {
                GameStore.setEarnedCoins(data.coinsReward);
            }

            if (data.comboCount !== undefined) {
                if (data.comboCount > 0) {
                    GameStore.setComboCount(data.comboCount);
                    GameStore.setLastComboTimestamp(Date.now());
                } else {
                    GameStore.resetCombo();
                }
            }
        }).catch((err) => {
            const errMsg = getBackendErrorMessage(err, "Не удалось завершить игру");
            showErrorToast(errMsg);
        }).finally(() => {
            refreshPlayerInfo();
            setIsFinishingGame(false);
            if (!hasShownResultsModal) {
                setHasShownResultsModal(true);
                triggerConfetti();
                routeNavigator.showModal(GAME_RESULTS_MODAL);
            }
        });
    }, [GameStore.gameID, GameStore.isActive, GameStore.timeLeft, hasShownResultsModal, isFinishingGame, isGameSessionLoaded, routeNavigator, showErrorToast]);

    useEffect(() => {
        if (activePanel !== GAME_PANEL) {
            return;
        }

        loadGameSession().finally(() => {
            refreshPlayerInfo();
        });
        fetchDailyAttemptPrice();
    }, [activePanel]);

    const handleSelectionComplete = (cells) => {
        if (isSubmittingWord || !GameStore.isActive) {
            return;
        }

        if (!GameStore.gameID || !Number.isFinite(Number(GameStore.gameID))) {
            showErrorToast("Игра ещё не создана");
            GameStore.clearSelection();
            return;
        }

        const selectedWord = cells
            .map((item) => GameStore.grid[item.row]?.[item.col] || "")
            .join("")
            .toLowerCase();

        if (selectedWord.length < 2) {
            showErrorToast("Минимум 2 буквы");
            GameStore.clearSelection();
            return;
        }

        setIsSubmittingWord(true);
        submitWord(GameStore.gameID, {
            mode: WORD_SEARCH_MODE,
            word: selectedWord,
            cells,
        }).then((resp) => {
            const data = resp?.data?.response ?? resp?.data;
            if (!data) {
                showErrorToast("Не удалось проверить слово");
                return;
            }

            if (data.isValid === false) {
                const reasons = {
                    time_expired: "Время вышло",
                    already_found: "Слово уже найдено",
                    path_required: "Проведите по буквам на поле",
                    invalid_path_direction: "Выберите слово по прямой линии",
                    non_continuous_path: "Выделение должно быть непрерывным",
                    not_in_dictionary: "Слово не найдено в словаре",
                    game_not_found: "Игра не найдена",
                };
                const reasonText = reasons[data.reason] ?? "Слово не засчитано";
                showErrorToast(reasonText);
                return;
            }

            if (data.newTotalScore !== undefined) {
                GameStore.setScore(data.newTotalScore);
                if (GameStore.bestScore < data.newTotalScore) {
                    GameStore.setBestScore(data.newTotalScore);
                }
            }

            if (data.coinsReward !== undefined) {
                GameStore.setEarnedCoins(data.coinsReward);
            }

            if (data.comboCount !== undefined) {
                GameStore.setComboCount(data.comboCount);
            }

            const lastWordFoundAt = data.lastWordFoundAt ?? data.last_word_found_at;
            const parsedLast = lastWordFoundAt ? Date.parse(lastWordFoundAt) : NaN;
            if (Number.isFinite(parsedLast)) {
                GameStore.setLastComboTimestamp(parsedLast);
            } else {
                GameStore.setLastComboTimestamp(Date.now());
            }

            if (Array.isArray(data.foundWords)) {
                GameStore.setFoundWords(data.foundWords);
            } else {
                GameStore.addFoundWord(selectedWord);
            }
            const lastFoundWord = Array.isArray(data.foundWords) && data.foundWords.length > 0
                ? data.foundWords[data.foundWords.length - 1]
                : selectedWord;
            const scoreAdded = Number(data.scoreAdded);
            const normalizedFoundWord = String(lastFoundWord ?? "").toLowerCase();
            const successText = Number.isFinite(scoreAdded) && scoreAdded > 0
                ? `Найдено: ${normalizedFoundWord} · ${scoreAdded} ${numberWordSuffix(scoreAdded, ["очко", "очка", "очков"])}`
                : `Найдено: ${normalizedFoundWord}`;
            showSuccessToast(successText);

            const foundWordPaths = data.foundWordPaths ?? data.found_word_paths;
            if (Array.isArray(foundWordPaths)) {
                GameStore.setFoundWordPaths(foundWordPaths);
            }
            const targetWordPaths = data.targetWordPaths ?? data.target_word_paths;
            if (Array.isArray(targetWordPaths)) {
                GameStore.setTargetWordPaths(targetWordPaths);
            }

            if (data.timeLeft !== undefined) {
                GameStore.setTimeLeft(data.timeLeft);
            }
            if (data.newTimeLeft !== undefined) {
                GameStore.setTimeLeft(data.newTimeLeft);
            }

            if (data.completed === true || data.isActive === false || data.is_active === false) {
                GameStore.setIsActive(false);
                if (!hasShownResultsModal) {
                    setHasShownResultsModal(true);
                    triggerConfetti();
                    routeNavigator.showModal(GAME_RESULTS_MODAL);
                }
            }
        }).catch((err) => {
            const errMsg = getBackendErrorMessage(err, "Не удалось проверить слово");
            showErrorToast(errMsg);
        }).finally(() => {
            GameStore.clearSelection();
            setIsSubmittingWord(false);
        });
    };

    const handleSelectionChange = (cells) => {
        GameStore.setSelectionCells(cells);
    };

    const handleBackButton = () => {
        GameStore.clearSelection();
        return isFirstPage ? routeNavigator.push("/") : routeNavigator.replace("/");
    };

    const navigationBlocker = useCallback(({currentLocation, nextLocation}) => {
        if (activePanel !== GAME_PANEL || !GameStore.isActive) {
            return false;
        }

        if (allowExitOnceRef.current) {
            allowExitOnceRef.current = false;
            return false;
        }

        const currentPath = currentLocation?.pathname || "";
        const nextPath = nextLocation?.pathname || "";
        return currentPath === "/game" && nextPath !== "/game";
    }, [activePanel]);

    const exitBlocker = useBlocker(navigationBlocker);

    useEffect(() => {
        if (activePanel !== GAME_PANEL) {
            setIsExitAlertOpen(false);
            return;
        }

        if (exitBlocker?.state === "blocked") {
            setIsExitAlertOpen(true);
        }
    }, [activePanel, exitBlocker?.state]);

    const handleExitStay = () => {
        if (exitBlocker?.reset) {
            exitBlocker.reset();
        }
        setIsExitAlertOpen(false);
    };

    const handleExitLeave = () => {
        GameStore.clearSelection();
        if (exitBlocker?.proceed) {
            setIsExitAlertOpen(false);
            allowExitOnceRef.current = true;
            exitBlocker.proceed();
            return;
        }
        setIsExitAlertOpen(false);
        return isFirstPage ? routeNavigator.push("/") : routeNavigator.replace("/");
    };

    const exitAlert = isExitAlertOpen ? (
        <PopoutWrapper onClick={handleExitStay}>
            <div className="w-full max-w-sm px-4">
                <div className={`flex flex-col gap-3 rounded-xl bg-gray-200 border border-gray-300/60 px-5 py-4 shadow-sm ${MODAL_CARD_CLASS}`}>
                    <div className={`text-lg font-semibold ${MODAL_HEADER_CLASS}`}>Выйти из игры?</div>
                    <div className={`text-sm ${MODAL_SUBHEADER_CLASS}`}>
                        Таймер продолжит отсчёт, игра не остановится.
                    </div>
                    <div className="flex flex-col gap-2 pt-1">
                        <Button
                            size="l"
                            mode="secondary"
                            className={MODAL_SECONDARY_BUTTON_CLASS}
                            onClick={handleExitLeave}
                        >
                            Выйти
                        </Button>
                        <Button
                            size="l"
                            mode="primary"
                            className={MODAL_PRIMARY_BUTTON_CLASS}
                            onClick={handleExitStay}
                        >
                            Остаться
                        </Button>
                    </div>
                </div>
            </div>
        </PopoutWrapper>
    ) : null;

    const handleUseHint = (type) => {
        if (!GameStore.gameID) {
            showErrorToast("Игра ещё не создана");
            return;
        }

        useHint(GameStore.gameID, type).then((resp) => {
            const data = resp?.data?.response ?? resp?.data;
            if (!data) {
                showErrorToast("Не удалось применить подсказку");
                return;
            }

            const hintWallet = data.hintWallet ?? data.hint_wallet;
            const hintLimits = data.limits ?? data.hintLimits ?? data.hint_limits;
            const {wallet, limits} = normalizeHintState(hintWallet, hintLimits);
            GameStore.setHintWallet(wallet);
            GameStore.setHintLimits(limits);

            const playerBalances = data.playerHintBalances ?? data.player_hint_balances;
            if (playerBalances) {
                GameStore.setPlayerHintBalances(playerBalances);
            }

            if (data.newTimeLeft !== undefined) {
                GameStore.setTimeLeft(data.newTimeLeft);
            }
            if (data.timeLeft !== undefined) {
                GameStore.setTimeLeft(data.timeLeft);
            }

            const gridPayload = data.grid ?? data.grid_letters ?? data.gridLetters;
            if (gridPayload !== undefined) {
                GameStore.setGrid(normalizeGridPayload(gridPayload));
            }

            const targetWordLengths = data.targetWordLengths ?? data.target_word_lengths;
            if (Array.isArray(targetWordLengths)) {
                GameStore.setTargetWordLengths(targetWordLengths);
            }
            const targetWordPaths = data.targetWordPaths ?? data.target_word_paths;
            if (Array.isArray(targetWordPaths)) {
                GameStore.setTargetWordPaths(targetWordPaths);
            }
            const foundWordPaths = data.foundWordPaths ?? data.found_word_paths;
            if (Array.isArray(foundWordPaths)) {
                GameStore.setFoundWordPaths(foundWordPaths);
            }

            if (type === WORD_CONTOUR_HINT_TYPE) {
                const hintReveal = data.hintRevealPath ?? data.hint_reveal_path;

                if (Array.isArray(hintReveal) && hintReveal.length > 0) {
                    setHintRevealPath(hintReveal);
                    if (hintRevealTimeoutRef.current) {
                        clearTimeout(hintRevealTimeoutRef.current);
                    }
                    hintRevealTimeoutRef.current = setTimeout(() => {
                        setHintRevealPath([]);
                        hintRevealTimeoutRef.current = null;
                    }, WORD_CONTOUR_REVEAL_MS);
                }
            } else {
                clearHintReveal();
            }

            GameStore.clearSelection();
            showSuccessToast("Подсказка использована");
        }).catch((err) => {
            const errMsg = getBackendErrorMessage(err, "Не удалось применить подсказку");
            showErrorToast(errMsg);
        });
    };

    const handleBuyHint = (type, options = {}) => {
        return buyHint(type, {gameId: GameStore.gameID}).then((resp) => {
            const player = resp?.data?.response ?? resp?.data;
            if (!player) {
                showErrorToast("Не удалось купить подсказку");
                return;
            }

            const playerBalances = player.hintBalances ?? player.hint_balances;
            const shouldUpdateBalance = !options?.skipBalanceUpdate;
            if (playerBalances && shouldUpdateBalance) {
                GameStore.setPlayerHintBalances(playerBalances);
            }

            if (player.coins !== undefined) {
                BonusStore.setCoins(player.coins);
            }

            if (!options?.skipBalanceUpdate) {
                showSuccessToast("Подсказка куплена");
            }
        }).catch((err) => {
            const errMsg = getBackendErrorMessage(err, "Не удалось купить подсказку");
            showErrorToast(errMsg);
            throw err;
        });
    };

    const handleStartNewGame = () => {
        if (availableAttempts <= 0 || isStartingNewGame) {
            return;
        }
        GameStore.flush();
        GameStore.setIsActive(false);
        setShouldShowOnboarding(true);
        refreshPlayerInfo();
    };

    const handleStartFirstGame = () => {
        if (isStartingNewGame) {
            return;
        }
        if (availableAttempts <= 0) {
            showErrorToast("Нет доступных жизней");
            return;
        }
        setShouldShowOnboarding(false);
        setIsStartingNewGame(true);
        GameStore.setDailyAttempts(Math.max(availableAttempts - 1, 0));
        startGameSession().finally(() => {
            refreshPlayerInfo();
            setIsStartingNewGame(false);
        });
    };

    const handleBuyDailyAttempts = () => {
        if (BonusStore.coins < dailyAttemptPrice) {
            showErrorToast("Не хватает монет");
            return;
        }

        add3DailyAttempts().then((resp) => {
            const player = resp.data.response;
            const totalDailyAttempts = player.available_daily_attempts ?? player.daily_attempts ?? 0;
            GameStore.setDailyAttempts(totalDailyAttempts);
            if (player.coins !== undefined) {
                BonusStore.setCoins(player.coins);
            }
            showSuccessToast("Жизни восстановлены");
        }).catch((err) => {
            const errMsg = getBackendErrorMessage(err, "Не удалось купить жизни");
            showErrorToast(errMsg);
        });
    };

    return <>
        {isConfettiVisible && (
            <Confetti
                key={confettiKey}
                recycle={false}
                width={width}
                height={height}
            />
        )}

        <div className="app-screen">
            <PanelHeader
                before={<PanelHeaderBack className="[&>svg]:p-0" onClick={handleBackButton}/>}
                separator={null}
            >
                Словариум
            </PanelHeader>

            <div className="flex-1 min-h-0">
                <SplitLayout popout={exitAlert || popout}>
                    <SplitCol>
                        <div className="app-screen__content app-screen__content--fill items-center overflow-hidden">
                            <div className="flex flex-col gap-3 w-full h-full min-h-0">

                        {loadingGameErrorCode === errPlayerDailyAttemptsAreEmpty && (
                            <AttemptsPanel
                                dailyAttemptPrice={dailyAttemptPrice}
                                canBuy={BonusStore.coins >= dailyAttemptPrice}
                                onBuy={handleBuyDailyAttempts}
                                availableAttempts={availableAttempts}
                                onStart={handleStartNewGame}
                                title="Жизни закончились"
                                description="Купите жизни и продолжайте игру без ожидания."
                                withCountdown
                                onCannotBuy={() => showErrorToast("Не хватает монет")}
                                onCountdownComplete={refreshPlayerInfo}
                            />
                        )}

                        {loadingGameErrorCode === errUnknownLoadingGame && (
                            <div className="flex flex-row px-4 py-2 items-center justify-center">
                                                <div className="flex w-full bg-gray-200 border border-gray-300/60 rounded px-4 py-6 items-center gap-3 shadow-sm">
                                    <p className="bold text-gray-800">
                                        При загрузке игры произошла неизвестная ошибка, но мы уже работаем над исправлением.
                                    </p>
                                </div>
                            </div>
                        )}
                        {loadingGameErrorCode === "" && (
                            <div className="flex flex-col gap-3 flex-1 min-h-0">
                                <StatusStrip/>

                                {!isGameSessionLoaded || shouldShowOnboarding ? null : GameStore.isActive ? null : (
                                    <div className="px-4">
                                        <div className="flex w-full bg-gray-200 border border-gray-300/60 rounded-xl px-4 py-6 items-center justify-center shadow-sm">
                                            <div className="flex flex-col items-center gap-3 w-full max-w-sm">
                                                <p className="text-xl bold text-gray-800 mb-2">Игра окончена 🎉</p>

                                                <Separator/>

                                                {availableAttempts > 0 ? (
                                                    <>
                                                        <Button
                                                            size="s"
                                                            mode="primary"
                                                            stretched
                                                            className={BUTTON_CLASSES}
                                                            onClick={handleStartNewGame}
                                                        >
                                                            Начать новую игру
                                                        </Button>
                                                        <div className="text-sm text-gray-700 text-center">
                                                            Начните новую игру и попробуйте улучшить результат 😉
                                                        </div>
                                                        <div className="text-xs text-gray-800">
                                                            {numberWordSuffix(availableAttempts, ["Осталась", "Осталось", "Осталось"])} {availableAttempts} {numberWordSuffix(availableAttempts, ["жизнь", "жизни", "жизней"])}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <AttemptsPanel
                                                        dailyAttemptPrice={dailyAttemptPrice}
                                                        canBuy={BonusStore.coins >= dailyAttemptPrice}
                                                        onBuy={handleBuyDailyAttempts}
                                                        availableAttempts={availableAttempts}
                                                        onStart={handleStartNewGame}
                                                        title="Жизни закончились"
                                                        description="Восстановите жизни и продолжайте играть без ожидания."
                                                        withCountdown={false}
                                                        onCannotBuy={() => showErrorToast("Не хватает монет")}
                                                        isLight
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {GameStore.isActive ? (
                                    <HintPanel onUseHint={handleUseHint} onBuyHint={handleBuyHint}/>
                                ) : null}

                                <div className="flex flex-col flex-1 min-h-0">
                                    <div className="flex-1 min-h-0 overflow-y-auto pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                                        {!isGameSessionLoaded ? null : shouldShowOnboarding ? (
                                            <div className="px-4">
                                                <div className="flex flex-col items-center justify-center text-center bg-gray-200 border border-gray-300/60 rounded-xl px-5 py-6 gap-3 shadow-sm">
                                                    <div className="text-lg bold text-gray-900">
                                                        Найдите как можно больше слов
                                                    </div>
                                                    <div className="text-sm text-gray-800">
                                                        Проводите по буквам в одну линию: горизонтально, вертикально или по диагонали.
                                                    </div>
                                                    <div className="text-xs text-gray-700">
                                                        Таймер начнёт идти после нажатия на кнопку.
                                                    </div>
                                                    <Button
                                                        size="s"
                                                        mode="primary"
                                                        stretched
                                                        className={BUTTON_CLASSES}
                                                        onClick={handleStartFirstGame}
                                                        disabled={isStartingNewGame}
                                                    >
                                                        <span className="text-sm">Начать</span>
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : !GameStore.isActive ? null : (
                                            <div className="flex flex-col gap-3">
                                                <GameBoard
                                                    onSelectionChange={handleSelectionChange}
                                                    onSelectionComplete={handleSelectionComplete}
                                                    isDisabled={!GameStore.isActive || isSubmittingWord}
                                                    showTargetContours={isDevEnv}
                                                    hintRevealPath={hintRevealPath}
                                                />
                                                <div className="px-4 text-xs text-gray-700 text-center">
                                                    Зажмите букву и ведите по линии, чтобы выделить слово.
                                                </div>
                                                <WordTargetsPanel/>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                            </div>
                        </div>
                    </SplitCol>
                </SplitLayout>
            </div>
        </div>
    </>;
});
